/** CAPEX models (Modules.Capex, /api/capex). The CEP-2 fields are built in; admins can add custom fields. */
import { NamedItem } from './common.models';
import { FormSchema, SubmissionData } from './form.models';
import { StoredFile } from './file.models';
import { EditableField, PendingStepDto, RequestStatus } from './request.models';
import { WorkflowAuditEntry } from './workflow.models';

export const CAPEX_APP_CODE = 'CAPEX';

/** CAPEX Global tables behind the purpose and nature-of-plan filters. */
export const CAPEX_PURPOSES_TABLE = 'global_purposes';
export const CAPEX_NATURES_OF_PLAN_TABLE = 'global_natures_of_plan';

/** GET /api/capex/form */
export interface CapexFormDto {
  formId: string;
  name: string;
  sections: { key: string; title: string }[];
  fields: CapexFieldDto[];
  customFieldsVersionNo: number;
  customFieldsSchema: FormSchema;
}

/** A built-in CEP-2 field. Types: text, longText, integer, decimal, date, month, assetType, globalItem, boolean. */
export interface CapexFieldDto {
  key: string;
  label: string;
  section: string;
  type: string;
  isRequired: boolean;
  isCalculated: boolean;
  maxLength?: number | null;
  min?: number | null;
  /** Global items: the CAPEX Global table the choices come from. */
  source?: string | null;
  helpText?: string | null;
  order: number;
}

/** A campus where I can raise CAPEX requests, with the designation I hold there. */
export interface CapexCampus {
  campusId: string;
  campusName: string;
  businessUnitId: string;
  businessUnitName: string;
  companyId: string;
  companyName: string;
  /** For reporting; empty while the business unit is in no cluster. */
  clusterId?: string | null;
  clusterName?: string | null;
  designationId: string;
  designationName: string;
  /** The asset types I may request here. */
  assetTypeIds: string[];
}

/** GET /api/capex/route: whether I may raise a request at the campus and (with an asset type) its workflow. */
export interface CapexRoute {
  canSubmit: boolean;
  hasWorkflow: boolean;
  workflowName?: string | null;
  approverFieldKeys: string[];
  message?: string | null;
}

/** A campus in the Entity → Business Unit → Campus filter cascade. */
export interface CapexBusinessUnitOption {
  id: string;
  name: string;
  companyId: string;
}

/** A campus in the Entity → Business Unit → Campus filter cascade. */
export interface CapexCampusOption {
  id: string;
  name: string;
  businessUnitId: string;
  companyId: string;
}

/** GET /api/capex/filter-options */
export interface CapexFilterOptions {
  companies: NamedItem[];
  businessUnits: CapexBusinessUnitOption[];
  campuses: CapexCampusOption[];
}

/** GET /api/capex/requests/visibility: what I can see beyond my own requests. */
export interface CapexVisibility {
  canViewOrganization: boolean;
  /** Own, Campus, Organisation or All. */
  scope: string;
  campusNames: string[];
}

export interface CapexRequestSummaryDto extends PendingStepDto {
  id: string;
  requestNo: string;
  status: RequestStatus;
  itemName?: string | null;
  quantity?: number | null;
  estimatedCostTotal?: number | null;
  /** A PDF was made for the request on its last submission or resubmission. */
  hasPdf?: boolean;
  assetTypeId?: string | null;
  assetTypeName?: string | null;
  companyId: string;
  companyName: string;
  businessUnitId: string;
  businessUnitName: string;
  campusId: string;
  campusName: string;
  clusterId?: string | null;
  clusterName?: string | null;
  requesterId: string;
  requesterName: string;
  workflowInstanceId?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  submittedAt?: string | null;
  completedAt?: string | null;
}

export interface CapexRequestDetailDto extends CapexRequestSummaryDto {
  attachments?: StoredFile[];
  timeline?: WorkflowAuditEntry[];
  values: SubmissionData;
  displayValues: Record<string, string>;
  customFieldsVersionNo: number;
  customFieldsSchema: FormSchema;
  customValues: SubmissionData;
  approverFieldKeys: string[];
  myEditableFields: EditableField[];
  canEdit: boolean;
  canSubmit: boolean;
  canResubmit: boolean;
}

export interface CapexRequestQuery {
  /** Kept for older callers; new code sends `statuses`. */
  status?: RequestStatus | '';
  statuses?: RequestStatus[];
  /** Created-date range as UTC instants: the start of the first local day, and the start of the day after the last. */
  createdFrom?: string;
  createdBefore?: string;
  purposeId?: string;
  natureOfPlanId?: string;
  /** Reporting: requests raised in business units of the cluster. */
  clusterId?: string;
  companyId?: string;
  businessUnitId?: string;
  campusId?: string;
  assetTypeId?: string;
  search?: string;
  sortBy?: 'date' | 'formNo' | 'totalCost';
  sortDirection?: 'asc' | 'desc';
  pageNumber?: number;
  pageSize?: number;
}
